import { computed, ref, type Ref } from "vue";
import type DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import type DSFieldset from "@/src/design_system/form_elements/fieldset/components/DSFieldset.vue";
import { blurActiveElementWithin } from "@/src/design_system/form_elements/shared/utils/activeElement";
import { getNextIndexByStep } from "@/src/design_system/form_elements/shared/utils/keyboardNavigation";
import type { DSButtonGroupOption, DSButtonGroupProps } from "../types/props";

export const useDSButtonGroup = (props: DSButtonGroupProps, model: Ref<string | undefined>) => {
  const buttonRefs = ref<InstanceType<typeof DSButton>[]>([]);
  const fieldsetRef = ref<InstanceType<typeof DSFieldset>>();
  const rootElement = () => fieldsetRef.value?.$el as HTMLFieldSetElement | undefined;
  const selectedOption = computed(() => props.options.find(option => option.value === model.value));
  const enabledOptions = computed(() => props.options.filter(option => !isDisabled(option)));
  const tabStop = computed(
    () => enabledOptions.value.find(option => option.value === model.value) ?? enabledOptions.value[0]
  );

  const isDisabled = (option: DSButtonGroupOption) => !!(props.disabled || option.disabled);
  const isSelected = (option: DSButtonGroupOption) => option.value === model.value;
  const getTabindex = (option: DSButtonGroupOption) => (option === tabStop.value ? 0 : -1);

  const selectOption = (option: DSButtonGroupOption) => {
    if (isDisabled(option) || isSelected(option)) return;
    model.value = option.value;
  };

  const focusOption = (option: DSButtonGroupOption | undefined) => {
    if (!option) return;
    buttonRefs.value.find(button => button.$props.value === option.value)?.$el.focus();
  };

  const focus = () => focusOption(tabStop.value);
  const blur = () => blurActiveElementWithin(rootElement());

  const onKeydown = (event: KeyboardEvent, option: DSButtonGroupOption) => {
    if (isDisabled(option) || event.altKey || event.ctrlKey || event.metaKey) return;

    const options = enabledOptions.value;
    const index = options.indexOf(option);
    let nextIndex: number;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = getNextIndexByStep(index, options.length, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = getNextIndexByStep(index, options.length, -1);
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = options.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectOption(options[nextIndex]);
    focusOption(options[nextIndex]);
  };

  return {
    buttonRefs,
    fieldsetRef,
    rootElement,
    selectedOption,
    isDisabled,
    isSelected,
    getTabindex,
    selectOption,
    onKeydown,
    focus,
    blur,
  };
};
